import { Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { WebsiteElement } from '@way/website-themes/themes/minimal_website/home-page-preview/home-page-preview.component';
import { HeroPanelData } from '../components/template/panels/hero-panel';
import { FooterData } from '../components/footer-panel/footer-panel.component';
import { BrandingData } from '../components/branding-panel/branding-panel.component';
import {
  buttonNavigationToHashLink,
  linkToButtonNavigation,
} from '../shared/panel-button-navigation';
import { mapContactBusinessHours } from '../../shared/utils/map-contact-business-hours.util';
import { resolveFooterQuickLinks } from '../../shared/utils/footer-quick-links.util';

export interface PopulateFooterResult {
  footerData: FooterData;
  websiteElements: WebsiteElement[];
}

/**
 * Pure transformation service for the website builder.
 *
 * Every method here takes raw API / template data and current editor state as
 * plain parameters, derives the new editor state, and returns it.  The only
 * side-effects are mutations to WebsiteElement objects that are passed in by
 * reference (the same objects the component holds) — no Angular state, no
 * subscriptions.
 */
@Injectable({ providedIn: 'root' })
export class TemplatePopulatorService {
  constructor(private logger: LoggerService) {}

  // ─── Layout helpers ────────────────────────────────────────────────────────

  /** Convert any layout representation from the API into the hero panel union type. */
  convertLayoutForHeroPanel(
    layoutValue: any
  ):
    | 'layout_1'
    | 'layout_2'
    | 'layout_3'
    | 'layout_4'
    | 'layout_5'
    | 'layout_6'
    | 'layout_7'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7' {
    if (!layoutValue) return 'layout_1';
    if (typeof layoutValue === 'string') {
      if (layoutValue.startsWith('layout_')) {
        return layoutValue as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
      if (/^[1-7]$/.test(layoutValue)) {
        return `layout_${layoutValue}` as
          'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
      }
    }
    if (typeof layoutValue === 'number' && layoutValue >= 1 && layoutValue <= 7) {
      return `layout_${layoutValue}` as
        'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7';
    }
    return 'layout_1';
  }

  /** Extract the layout value from an element's content object. */
  extractLayoutFromContent(
    content: any
  ):
    | 'layout_1'
    | 'layout_2'
    | 'layout_3'
    | 'layout_4'
    | 'layout_5'
    | 'layout_6'
    | 'layout_7'
    | '1'
    | '2'
    | '3'
    | '4'
    | '5'
    | '6'
    | '7'
    | undefined {
    if (content.layout && typeof content.layout === 'object' && content.layout.type) {
      return this.convertLayoutForHeroPanel(content.layout.type);
    }
    if (content.layout_type) {
      return this.convertLayoutForHeroPanel(content.layout_type);
    }
    if (content.layout && typeof content.layout === 'string') {
      return this.convertLayoutForHeroPanel(content.layout);
    }
    return undefined;
  }

  /** Strip numeric suffix from a section key (e.g. info_section_1 → info_section). */
  normalizeSectionKey(sectionKey: string): string {
    const match = sectionKey.match(/^(.+?)(_\d+)?$/);
    return match && match[1] ? match[1] : sectionKey;
  }

  /** Derive a page-type string from a navigation link value. */
  extractPageTypeFromLink(link: string): string {
    if (!link) return 'home';
    const normalizedLink = link
      .replace(/^#+|\/+$/g, '')
      .replace(/^\/+/, '')
      .toLowerCase();
    if (normalizedLink === '' || normalizedLink === 'home' || normalizedLink === '/') return 'home';
    if (
      normalizedLink === 'expresswashes' ||
      normalizedLink === 'express-washes' ||
      normalizedLink.includes('express-wash')
    )
      return 'express-wash-packages';
    if (
      normalizedLink === 'expressdetails' ||
      normalizedLink === 'express-details' ||
      normalizedLink.includes('express-detail') ||
      normalizedLink.includes('express-auto')
    )
      return 'express-auto-details';
    if (
      normalizedLink === 'fleets' ||
      normalizedLink === 'fleet' ||
      normalizedLink.includes('fleet')
    )
      return 'fleet-management';
    if (
      normalizedLink === 'coupons' ||
      normalizedLink === 'coupon' ||
      normalizedLink.includes('coupon')
    )
      return 'coupons';
    return normalizedLink || 'home';
  }

  /** Check whether navigation should be regenerated from the current pages list. */
  shouldRegenerateNavigation(currentNav: any[], pages: any[]): boolean {
    if (!pages || pages.length === 0) return false;
    if (currentNav.length !== pages.length) return true;
    const pageNames = pages.map((p: any) => p.name);
    const navLabels = currentNav.map(n => n.label);
    for (const pageName of pageNames) {
      if (!navLabels.includes(pageName)) return true;
    }
    return false;
  }

  // ─── Default data ───────────────────────────────────────────────────────────

  /** Return the default booking form field definitions. */
  getDefaultFormFields(): any[] {
    return [
      {
        id: 'fullName',
        name: 'fullName',
        label: 'Full Name',
        type: 'text',
        placeholder: 'Enter your name',
        required: true,
      },
      {
        id: 'email',
        name: 'email',
        label: 'Email',
        type: 'email',
        placeholder: 'Enter your email',
        required: true,
      },
      {
        id: 'phone',
        name: 'phone',
        label: 'Phone',
        type: 'tel',
        placeholder: 'Enter your phone number',
        required: true,
      },
      {
        id: 'serviceType',
        name: 'serviceType',
        label: 'Service Type',
        type: 'select',
        required: true,
        options: [
          { value: '', label: 'Select Service', disabled: true },
          { value: 'express', label: 'Express Wash - $15' },
          { value: 'premium', label: 'Premium Wash - $35' },
        ],
      },
      {
        id: 'preferredDate',
        name: 'preferredDate',
        label: 'Preferred Date',
        type: 'date',
        placeholder: 'dd-mm-yyyy',
        required: true,
      },
      {
        id: 'preferredTime',
        name: 'preferredTime',
        label: 'Preferred Time',
        type: 'select',
        required: true,
        options: [
          { value: '', label: 'Select Time', disabled: true },
          { value: '08:00', label: '8:00 AM' },
          { value: '09:00', label: '9:00 AM' },
          { value: '10:00', label: '10:00 AM' },
          { value: '11:00', label: '11:00 AM' },
          { value: '12:00', label: '12:00 PM' },
          { value: '13:00', label: '1:00 PM' },
          { value: '14:00', label: '2:00 PM' },
          { value: '15:00', label: '3:00 PM' },
          { value: '16:00', label: '4:00 PM' },
          { value: '17:00', label: '5:00 PM' },
        ],
      },
      {
        id: 'specialRequests',
        name: 'specialRequests',
        label: 'Special Requests or Notes',
        type: 'textarea',
        placeholder: 'Any special request or vehicle details',
        required: false,
      },
    ];
  }

  // ─── Element builders ───────────────────────────────────────────────────────

  /**
   * Build the initial websiteElements array from a page's JSON sections.
   * Falls back to ensureBaseElements when there are no sections.
   * Returns a new array.
   */
  buildElementsFromJsonSections(currentJsonData: any, templateData: any): WebsiteElement[] {
    const pageSections = currentJsonData?.page?.sections || templateData?.json_data?.page?.sections;

    if (!pageSections || typeof pageSections !== 'object' || Array.isArray(pageSections)) {
      return this.ensureBaseElements([]);
    }

    const sectionKeyMapping: Record<string, string> = {
      hero_section: 'hero',
      info_section: 'section',
      testimonials_section: 'testimonials',
      booking_section: 'booking',
      contact_section: 'contact',
      services_section: 'services',
      package_section: 'packages',
      more_information_section: 'services',
      form_section: 'form',
      coupon_card: 'coupon',
      instructions_section: 'instructions',
    };

    const elements: WebsiteElement[] = [];

    elements.push({ id: 'header', type: 'header', content: {}, selected: false } as any);

    const heroSectionKey = Object.keys(pageSections).find(
      key => this.normalizeSectionKey(key) === 'hero_section'
    );
    const otherSectionKeys = Object.keys(pageSections).filter(
      key => this.normalizeSectionKey(key) !== 'hero_section'
    );

    const getTitle = (title: any): string => {
      if (typeof title === 'string') return title;
      if (title?.label) return title.label;
      return '';
    };
    const getTitleStyle = (title: any): any => {
      if (typeof title === 'object' && title?.style) return title.style;
      return null;
    };
    const normalizeLayout = (section: any): any => {
      const layoutValue: any = section.layout?.type;
      if (layoutValue) {
        if (typeof layoutValue === 'string' && layoutValue.startsWith('layout_'))
          return layoutValue;
        if (typeof layoutValue === 'string' && /^[1-7]$/.test(layoutValue))
          return `layout_${layoutValue}`;
        if (typeof layoutValue === 'number' && layoutValue >= 1 && layoutValue <= 7)
          return `layout_${layoutValue}`;
      }
      return 'layout_1';
    };

    if (heroSectionKey) {
      const section = pageSections[heroSectionKey];
      const normalizedKey = this.normalizeSectionKey(heroSectionKey);
      const mappedType = sectionKeyMapping[normalizedKey] || normalizedKey;
      elements.push({
        id: mappedType,
        type: mappedType,
        selected: false,
        originalKey: heroSectionKey,
        content: {
          title: getTitle(section.title),
          titleStyle: getTitleStyle(section.title),
          description: section.description,
          buttonText: section.button?.label,
          buttonLink: section.button?.link || section.button?.action || '',
          imageUrl: section.image?.src,
          image: section.image || {
            src: section.image?.src || '',
            alt: section.image?.alt || '',
            width: '100%',
            height: 'auto',
            scaletype: 'fit',
          },
          layout: normalizeLayout(section),
          backgroundColor: section.layout?.background_color,
        },
      } as any);
    }

    otherSectionKeys.forEach(sectionKey => {
      const normalizedKey = this.normalizeSectionKey(sectionKey);
      const mappedType = sectionKeyMapping[normalizedKey] || normalizedKey;
      const section = pageSections[sectionKey];

      if (normalizedKey === 'info_section') {
        const elementId = sectionKey === 'info_section' ? 'welcome' : `welcome_${sectionKey}`;
        elements.push({
          id: elementId,
          type: 'section',
          content: {
            backgroundColor: section.layout?.background_color,
          },
          selected: false,
          originalKey: sectionKey,
        } as any);
      } else if (mappedType) {
        let elementContent: any = {};
        if (normalizedKey === 'package_section' && section) {
          elementContent = {
            backgroundColor: section.backgroundColor || section.layout?.background_color || null,
          };
        } else if (normalizedKey === 'more_information_section' && section) {
          elementContent = {
            title: section.title?.label || section.title || 'More Information',
            subtitle: section.subtitle?.label || section.subtitle || '',
            services: section.services || [],
            backgroundColor: section.layout?.background_color || '#F5F5F5',
          };
        } else if (sectionKey === 'hero_section' && section) {
          elementContent = {
            title: getTitle(section.title),
            titleStyle: getTitleStyle(section.title),
            description: section.description,
            buttonText: section.button?.label,
            buttonLink: section.button?.link || section.button?.action || '',
            imageUrl: section.image?.src,
            layout: normalizeLayout(section),
            backgroundColor: section.layout?.background_color,
          };
        }
        elements.push({
          id: mappedType,
          type: mappedType,
          content: elementContent,
          selected: false,
          originalKey: sectionKey,
        } as any);
      }
    });

    elements.push({ id: 'footer', type: 'footer', content: {}, selected: false } as any);

    const bookingElement = elements.find(e => e.id === 'booking');
    if (bookingElement) {
      const content = bookingElement.content as any;
      if (!content || !content.formFields || content.formFields.length === 0 || !content.button) {
        (bookingElement.content as any) = {
          ...(content || {}),
          formFields:
            content?.formFields?.length > 0 ? content.formFields : this.getDefaultFormFields(),
          button: content?.button || {
            text: 'Book Appointment',
            backgroundColor: '#1B5BFF',
            textColor: '#FFFFFF',
          },
        };
      }
    }

    return elements;
  }

  /** Ensure the given elements array has at least the base set of elements. Returns (possibly new) array. */
  ensureBaseElements(websiteElements: WebsiteElement[]): WebsiteElement[] {
    if (Array.isArray(websiteElements) && websiteElements.length > 0) {
      const bookingElement = websiteElements.find(e => e.id === 'booking');
      if (bookingElement) {
        const content = bookingElement.content as any;
        if (!content || !content.formFields || content.formFields.length === 0 || !content.button) {
          (bookingElement.content as any) = {
            ...(content || {}),
            formFields:
              content?.formFields?.length > 0 ? content.formFields : this.getDefaultFormFields(),
            button: content?.button || {
              text: 'Book Appointment',
              backgroundColor: '#1B5BFF',
              textColor: '#FFFFFF',
            },
          };
        }
      }
      return websiteElements;
    }
    return [
      { id: 'header', type: 'header', content: {}, selected: false } as any,
      { id: 'hero', type: 'hero', content: {}, selected: false } as any,
      { id: 'welcome', type: 'section', content: {}, selected: false } as any,
      { id: 'testimonials', type: 'testimonials', content: {}, selected: false } as any,
      { id: 'services', type: 'services', content: {}, selected: false } as any,
      {
        id: 'booking',
        type: 'booking',
        content: { formFields: this.getDefaultFormFields() },
        selected: false,
      } as any,
      { id: 'contact', type: 'contact', content: {}, selected: false } as any,
      { id: 'footer', type: 'footer', content: {}, selected: false } as any,
    ];
  }

  /**
   * Apply a raw legacy JSON structure to the elements array (in-place mutation).
   * Returns the new heroPanelData (replaced, not mutated).
   */
  populateFromJson(
    json: any,
    websiteElements: WebsiteElement[],
    currentHeroPanelData: HeroPanelData
  ): HeroPanelData {
    if (!json || typeof json !== 'object') return currentHeroPanelData;

    if (json.header) {
      const headerElement = websiteElements.find(e => e.id === 'header');
      if (headerElement) {
        headerElement.content = {
          logo: json.header.logo,
          navigation: json.header.navigation || [],
          ctaButton: json.header.ctaButton,
          mobileMenu: json.header.mobileMenu,
        } as any;
      }
    }

    let newHeroPanelData = currentHeroPanelData;
    if (json.hero) {
      const heroElement = websiteElements.find(e => e.id === 'hero');
      if (heroElement) {
        heroElement.content = {
          title: json.hero.title,
          subtitle: json.hero.subtitle,
          description: json.hero.description,
          buttonText: json.hero.ctaButton?.text,
          imageUrl: json.hero.heroImage?.image,
        } as any;
      }
      const imageSrc =
        json.hero.heroImage?.image || json.hero.image?.src || currentHeroPanelData.image?.src || '';
      const imageScaletype =
        json.hero.image?.scaletype || currentHeroPanelData.image?.scaletype || 'fit';
      newHeroPanelData = {
        heading: json.hero.title || currentHeroPanelData.heading,
        paragraph: json.hero.description || currentHeroPanelData.paragraph,
        image: {
          src: imageSrc,
          alt: json.hero.image?.alt || currentHeroPanelData.image?.alt || '',
          width: json.hero.image?.width || currentHeroPanelData.image?.width || '420px',
          height: json.hero.image?.height || currentHeroPanelData.image?.height || '320px',
          scaletype: imageScaletype,
        },
        imageUrl: imageSrc,
        imageFit: imageScaletype,
        buttonText: json.hero.ctaButton?.text || currentHeroPanelData.buttonText,
        buttonNavigation: json.hero.ctaButton?.link || currentHeroPanelData.buttonNavigation,
        backgroundColor: currentHeroPanelData.backgroundColor,
      };
    }

    if (Array.isArray(json.sections)) {
      const welcome = json.sections.find((s: any) => s.id === 'welcome' || s.type === 'welcome');
      if (welcome) {
        const welcomeElement = websiteElements.find(e => e.id === 'welcome');
        if (welcomeElement) {
          welcomeElement.content = {
            title: welcome.title,
            description: welcome.description,
            buttonText: welcome.ctaButton?.text,
            imageUrl: welcome.image?.image,
            layout: (welcome.layout_type || '1') as '1' | '2' | '3',
          } as any;
        }
      }
    }

    if (json.testimonials) {
      const testimonialsElement = websiteElements.find(e => e.id === 'testimonials');
      if (testimonialsElement) {
        testimonialsElement.content = {
          title: json.testimonials.title,
          description: json.testimonials.subtitle,
          testimonials: json.testimonials.testimonials || [],
        } as any;
      }
    }

    if (json.services) {
      const servicesElement = websiteElements.find(e => e.id === 'services');
      if (servicesElement) {
        servicesElement.content = {
          title: json.services.title,
          description: json.services.description,
          buttonText: json.services.ctaButton?.text,
          imageUrl: json.services.services?.[0]?.image || '',
        } as any;
      }
    }

    if (json.booking) {
      const bookingElement = websiteElements.find(e => e.id === 'booking');
      if (bookingElement) {
        bookingElement.content = {
          title: json.booking.title,
          description: json.booking.subtitle,
          formFields: json.booking.formFields || [],
          availableServices: json.booking.availableServices || [],
        } as any;
      }
    }

    if (json.contact) {
      const contactElement = websiteElements.find(e => e.id === 'contact');
      if (contactElement) {
        contactElement.content = {
          title: json.contact.title,
          description: json.contact.subtitle,
          map: json.contact.map,
          contactInfo: json.contact.contactInfo,
          socialLinks: json.contact.socialLinks || [],
          businessHours: json.contact.businessHours || [],
        } as any;
      }
    }

    return newHeroPanelData;
  }

  // ─── Populate from template ─────────────────────────────────────────────────

  /**
   * Populate hero panel data + update the hero element's content from template or page JSON.
   * Mutates websiteElements in-place; returns a new HeroPanelData object.
   */
  populateHero(
    currentJsonData: any,
    templateData: any,
    currentHeroPanelData: HeroPanelData,
    websiteElements: WebsiteElement[]
  ): HeroPanelData {
    const heroData =
      currentJsonData?.page?.sections?.hero_section ||
      templateData?.json_data?.page?.sections?.hero_section ||
      templateData?.json_data?.hero ||
      currentJsonData?.hero;

    this.logger.debug('🟢 populateHero CALLED');
    this.logger.debug('🟢 heroData resolved to:', heroData);

    if (!heroData) {
      this.logger.warn('🟢 No hero data found in template or current page data');
      return currentHeroPanelData;
    }

    let titleText = '';
    if (typeof heroData.title === 'string') {
      titleText = heroData.title;
    } else if (typeof heroData.title === 'object' && heroData.title !== null) {
      titleText = heroData.title.label || '';
    }
    if (!titleText) titleText = currentHeroPanelData.heading || '';

    const titleStyle = this.extractTitleStyleFromApi(heroData.title?.style);

    const backgroundColor =
      heroData.layout?.background_color ||
      heroData.background_color ||
      currentHeroPanelData.backgroundColor;
    const descriptionText =
      heroData.subtitle || heroData.description || currentHeroPanelData.paragraph || '';
    const existingImage = heroData.image || {};
    const imageSrc =
      existingImage.src || heroData.heroImage?.image || currentHeroPanelData.image?.src || '';
    const imageScaletype =
      existingImage.scaletype || currentHeroPanelData.image?.scaletype || 'fit';

    const newHeroPanelData: HeroPanelData = {
      heading: titleText,
      paragraph: descriptionText,
      titleStyle: titleStyle || undefined,
      image: {
        src: imageSrc,
        alt: existingImage.alt || currentHeroPanelData.image?.alt || '',
        width: existingImage.width || currentHeroPanelData.image?.width || '420px',
        height: existingImage.height || currentHeroPanelData.image?.height || '320px',
        scaletype: imageScaletype,
      },
      imageUrl: imageSrc,
      imageFit: imageScaletype,
      buttonText:
        heroData.button?.label || heroData.ctaButton?.text || currentHeroPanelData.buttonText,
      buttonNavigation:
        heroData.button?.link || heroData.ctaButton?.link || currentHeroPanelData.buttonNavigation,
      backgroundColor,
      layout: this.convertLayoutForHeroPanel(
        heroData.layout?.type ||
          heroData.layout?.layout ||
          heroData.layout_type ||
          currentHeroPanelData.layout ||
          'layout_1'
      ),
    };

    const heroElement = websiteElements.find(e => e.id === 'hero');
    if (heroElement) {
      let layoutValue: any =
        heroData.layout?.type || heroData.layout?.layout || heroData.layout_type;
      if (layoutValue) {
        if (typeof layoutValue === 'string' && layoutValue.startsWith('layout_')) {
          /* keep */
        } else if (typeof layoutValue === 'string' && /^[1-7]$/.test(layoutValue))
          layoutValue = `layout_${layoutValue}`;
        else if (typeof layoutValue === 'number' && layoutValue >= 1 && layoutValue <= 7)
          layoutValue = `layout_${layoutValue}`;
      } else {
        layoutValue = 'layout_1';
      }

      heroElement.content = {
        title: titleText,
        titleStyle: titleStyle || undefined,
        backgroundColor: backgroundColor || undefined,
        description: descriptionText,
        buttonText: heroData.button?.label || heroData.ctaButton?.text,
        buttonLink: heroData.button?.link || heroData.ctaButton?.link || '',
        imageUrl: heroData.image?.src || heroData.heroImage?.image,
        image: heroData.image || {
          src: heroData.heroImage?.image || '',
          alt: '',
          width: '420px',
          height: '320px',
          scaletype: 'fit',
        },
        layout: layoutValue,
      };
    } else {
      this.logger.warn('🟢 Hero element not found in website elements');
    }

    if (titleText || descriptionText) {
      if (!currentJsonData) return newHeroPanelData;
      if (!currentJsonData.page) currentJsonData.page = {};
      if (!currentJsonData.page.sections) currentJsonData.page.sections = {};
      if (!currentJsonData.page.sections.hero_section)
        currentJsonData.page.sections.hero_section = {};
      const heroSection = currentJsonData.page.sections.hero_section;
      if (!heroSection.title || typeof heroSection.title !== 'object') heroSection.title = {};
      if (titleText && !heroSection.title.label) heroSection.title.label = titleText;
      if (descriptionText && !heroSection.description) heroSection.description = descriptionText;
    }

    this.logger.debug('🟢 Hero data populated from template:', newHeroPanelData);
    return newHeroPanelData;
  }

  /**
   * Populate info/welcome element(s) from template page sections.
   * Mutates websiteElements in-place.
   */
  populateSections(
    currentJsonData: any,
    templateData: any,
    websiteElements: WebsiteElement[]
  ): void {
    const pageSections = currentJsonData?.page?.sections || templateData?.json_data?.page?.sections;
    const sectionsArray = templateData?.json_data?.sections;

    if (!pageSections && !sectionsArray) {
      this.logger.warn('No sections data found in template');
      return;
    }

    if (pageSections && typeof pageSections === 'object' && !Array.isArray(pageSections)) {
      this.logger.debug('Populating sections from new template structure:', pageSections);

      Object.keys(pageSections).forEach(sectionKey => {
        const normalizedKey = this.normalizeSectionKey(sectionKey);
        if (normalizedKey !== 'info_section') return;

        const aboutSection = pageSections[sectionKey];
        if (!aboutSection || typeof aboutSection !== 'object') return;

        const sectionButton = aboutSection.button || aboutSection.ctaButton;
        const sectionImage = aboutSection.image || aboutSection.heroImage;
        const titleText = aboutSection.title?.label || aboutSection.title;
        const titleStyleObj = aboutSection.title?.style;
        const titleStyle =
          titleStyleObj && typeof titleStyleObj === 'object'
            ? titleStyleObj.text_style
              ? {
                  fontWeight: titleStyleObj.text_style.fontWeight || null,
                  fontStyle: titleStyleObj.text_style.fontStyle || null,
                  textDecoration: titleStyleObj.text_style.textDecoration || null,
                  textAlign: titleStyleObj.text_style.textAlign || null,
                }
              : {
                  fontWeight: titleStyleObj.fontWeight || null,
                  fontStyle: titleStyleObj.fontStyle || null,
                  textDecoration: titleStyleObj.textDecoration || null,
                  textAlign: titleStyleObj.textAlign || null,
                }
            : null;
        const backgroundColor = aboutSection.layout?.background_color || null;
        const content = {
          title: titleText,
          titleStyle: titleStyle || undefined,
          backgroundColor: backgroundColor || undefined,
          description: aboutSection.description,
          buttonText: sectionButton?.label || sectionButton?.text,
          buttonLink: sectionButton?.link || '',
          imageUrl: sectionImage?.src || sectionImage?.image,
          layout: String(aboutSection.layout?.type || aboutSection.layout_type || '2') as
            '1' | '2' | '3',
        };

        if (sectionKey === 'info_section') {
          const welcomeElement = websiteElements.find(e => e.id === 'welcome');
          if (welcomeElement) {
            welcomeElement.content = content;
            this.logger.debug('✅ About section populated:', content);
          }
        } else {
          const elementId = `welcome_${sectionKey}`;
          const infoElement = websiteElements.find(
            e => e.id === elementId || (e as any).originalKey === sectionKey
          );
          if (infoElement) {
            infoElement.content = content;
            this.logger.debug(`✅ Info section variant ${sectionKey} populated.`);
          } else {
            this.logger.warn(`⚠️ Element ${elementId} not found.`);
          }
        }
      });
    } else if (Array.isArray(sectionsArray)) {
      const welcomeSectionData = sectionsArray.find(
        (s: any) => s.id === 'welcome' || s.type === 'welcome'
      );
      if (welcomeSectionData) {
        const welcomeElement = websiteElements.find(e => e.id === 'welcome');
        if (welcomeElement) {
          const titleText = welcomeSectionData.title?.label || welcomeSectionData.title;
          const titleStyleObj = welcomeSectionData.title?.style;
          const titleStyle =
            titleStyleObj && typeof titleStyleObj === 'object'
              ? titleStyleObj.text_style
                ? {
                    fontWeight: titleStyleObj.text_style.fontWeight || null,
                    fontStyle: titleStyleObj.text_style.fontStyle || null,
                    textDecoration: titleStyleObj.text_style.textDecoration || null,
                    textAlign: titleStyleObj.text_style.textAlign || null,
                  }
                : {
                    fontWeight: titleStyleObj.fontWeight || null,
                    fontStyle: titleStyleObj.fontStyle || null,
                    textDecoration: titleStyleObj.textDecoration || null,
                    textAlign: titleStyleObj.textAlign || null,
                  }
              : null;
          welcomeElement.content = {
            title: titleText,
            titleStyle: titleStyle || undefined,
            backgroundColor: welcomeSectionData.layout?.background_color || undefined,
            description: welcomeSectionData.description,
            buttonText: welcomeSectionData.ctaButton?.text,
            imageUrl: welcomeSectionData.image?.image,
            layout: (welcomeSectionData.layout_type || '1') as '1' | '2' | '3',
          };
          this.logger.debug('Welcome section populated from template.');
        }
      } else {
        this.logger.warn('Welcome section not found in template data');
      }
    }
  }

  /**
   * Populate testimonials element content from template data.
   * Mutates websiteElements in-place.
   */
  populateTestimonials(templateData: any, websiteElements: WebsiteElement[]): void {
    const testimonialsData =
      templateData?.json_data?.page?.sections?.testimonials_section ||
      templateData?.json_data?.testimonials;

    if (!testimonialsData) {
      this.logger.warn('⚠️ No testimonials data found in template');
      return;
    }

    const testimonialsElement = websiteElements.find(e => e.id === 'testimonials');
    if (!testimonialsElement) {
      this.logger.warn('⚠️ Testimonials element not found');
      return;
    }

    const mappedTestimonials = (testimonialsData.testimonials || []).map((t: any) => ({
      id: t.id || t.name || Math.random().toString(),
      text: t.review || t.text || '',
      author: { name: t.name, title: t.title, avatar: t.avatar },
      rating: t.rating || 5,
      date: t.date,
    }));

    const titleText = this.extractTitleText(testimonialsData.title);
    const titleStyle = this.extractTitleStyleFromApi(testimonialsData.title?.style);

    testimonialsElement.content = {
      title: titleText,
      titleStyle: titleStyle || undefined,
      backgroundColor: testimonialsData.layout?.background_color || undefined,
      cardColor: testimonialsData.layout?.card_color || undefined,
      description: testimonialsData.description || testimonialsData.subtitle || '',
      testimonials: mappedTestimonials,
    } as any;
    this.logger.debug('✅ Testimonials section populated.');
  }

  /**
   * Populate booking element content from template data.
   * Mutates websiteElements in-place.
   */
  populateBooking(
    currentJsonData: any,
    templateData: any,
    websiteElements: WebsiteElement[]
  ): void {
    const bookingData =
      currentJsonData?.page?.sections?.booking_section ||
      templateData?.json_data?.page?.sections?.booking_section ||
      templateData?.json_data?.booking;

    const bookingElement = websiteElements.find(e => e.id === 'booking');
    if (!bookingElement) {
      this.logger.warn('Booking element not found');
      return;
    }

    if (!bookingData) {
      bookingElement.content = {
        ...bookingElement.content,
        formFields: this.getDefaultFormFields(),
        availableServices: [],
        button: { text: 'Book Appointment', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' },
      };
      return;
    }

    const titleText = this.extractTitleText(bookingData.title);
    const titleStyle = this.extractTitleStyleFromApi(bookingData.title?.style);

    const templateFormFields = bookingData.form_fields || bookingData.formFields || [];
    const buttonData = bookingData.button;
    const button = buttonData
      ? {
          text: buttonData.label || buttonData.text || 'Book Appointment',
          backgroundColor:
            buttonData.style?.background ||
            buttonData.style?.background_color ||
            buttonData.backgroundColor ||
            '#1B5BFF',
          textColor:
            buttonData.style?.color ||
            buttonData.style?.text_color ||
            buttonData.textColor ||
            '#FFFFFF',
        }
      : { text: 'Book Appointment', backgroundColor: '#1B5BFF', textColor: '#FFFFFF' };

    bookingElement.content = {
      title: titleText,
      titleStyle: titleStyle || undefined,
      backgroundColor: bookingData.layout?.background_color || undefined,
      description: bookingData.subtitle || bookingData.description,
      formFields: templateFormFields.length > 0 ? templateFormFields : this.getDefaultFormFields(),
      availableServices: bookingData.availableServices || [],
      button,
    };
  }

  /**
   * Populate contact element content from template data.
   * Mutates websiteElements in-place; returns spread websiteElements for CD.
   */
  populateContact(templateData: any, websiteElements: WebsiteElement[]): WebsiteElement[] {
    const contactData =
      templateData?.json_data?.page?.sections?.contact_section || templateData?.json_data?.contact;

    if (!contactData) {
      this.logger.warn('⚠️ No contact data found in template');
      return websiteElements;
    }

    const contactElement = websiteElements.find(e => e.id === 'contact');
    if (!contactElement) {
      this.logger.warn('⚠️ Contact element not found');
      return websiteElements;
    }

    const socialLinks = (contactData.social_links || contactData.socialLinks || []).map(
      (social: any) => ({
        platform: social.platform,
        url: social.link || social.url,
        icon: social.icon,
        label: social.label || social.platform,
      })
    );

    let businessHours = mapContactBusinessHours(contactData);
    if (businessHours.length === 0) {
      businessHours = [
        { day: 'Monday - Friday', openTime: '8:00 AM - 6:00 PM', closed: false },
        { day: 'Saturday', openTime: '8:00 AM - 5:00 PM', closed: false },
        { day: 'Sunday', openTime: '9:00 AM - 4:00 PM', closed: false },
      ];
    }

    let mapEmbedUrl = contactData.map_embed_url || '';
    if (mapEmbedUrl && typeof mapEmbedUrl === 'string') {
      if (mapEmbedUrl.trim().startsWith('http://') || mapEmbedUrl.trim().startsWith('https://')) {
        mapEmbedUrl = mapEmbedUrl.trim();
      } else if (mapEmbedUrl.includes('<iframe') || mapEmbedUrl.includes('&lt;iframe')) {
        try {
          let srcMatch = mapEmbedUrl.match(/src\s*=\s*"([^"]+)"|src\s*=\s*'([^']+)'/i);
          if (srcMatch) {
            const extracted = srcMatch[1] || srcMatch[2];
            if (extracted)
              mapEmbedUrl = extracted
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/&amp;/g, '&');
          } else {
            const decoded = mapEmbedUrl
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>')
              .replace(/&quot;/g, '"');
            if (decoded !== mapEmbedUrl) {
              srcMatch = decoded.match(/src\s*=\s*"([^"]+)"|src\s*=\s*'([^']+)'/i);
              if (srcMatch) {
                const e = srcMatch[1] || srcMatch[2];
                if (e) mapEmbedUrl = e;
              }
            }
            if (!mapEmbedUrl || mapEmbedUrl.includes('<iframe')) {
              try {
                const doc = new DOMParser().parseFromString(
                  `<div>${mapEmbedUrl}</div>`,
                  'text/html'
                );
                const src = doc.querySelector('iframe')?.getAttribute('src');
                if (src) mapEmbedUrl = src;
              } catch {
                /* ignore */
              }
            }
          }
        } catch (error) {
          this.logger.error('❌ Error parsing map embed URL:', error);
          mapEmbedUrl = '';
        }
      }
    }

    const titleText = this.extractTitleText(contactData.title) || 'Contact Us';
    const titleStyle = this.extractTitleStyleFromApi(contactData.title?.style);

    const layoutType = contactData.layout?.type;
    const templateName = templateData?.name?.toLowerCase() || '';
    let defaultLayout: 'layout_1' | 'layout_2' = 'layout_1';
    if (layoutType === 'layout_2' || layoutType === '2') defaultLayout = 'layout_2';
    else if (layoutType === 'layout_1' || layoutType === '1') defaultLayout = 'layout_1';
    else if (templateName.includes('dark')) defaultLayout = 'layout_2';

    contactElement.content = {
      title: titleText,
      titleStyle: titleStyle || undefined,
      backgroundColor: contactData.layout?.background_color || undefined,
      description: contactData.subtitle || contactData.description || '',
      address: contactData.details?.location?.value || contactData.details?.address || '',
      phone:
        this.extractContactDetailString(contactData.details?.phone) ||
        this.extractContactDetailString(contactData.details?.contact),
      email: this.extractContactDetailString(contactData.details?.email),
      mapEmbedUrl,
      socialLinks,
      businessHours: businessHours.map(hour => ({
        day: hour.day || '',
        openTime: hour.openTime || '',
        closeTime: hour.openTime || '',
        closed: Boolean(hour.closed),
      })),
      layout: defaultLayout,
    };

    return [...websiteElements];
  }

  /**
   * Populate header element content from template / website data.
   * Mutates header element in-place; returns spread websiteElements for CD.
   */
  populateHeader(
    currentJsonData: any,
    templateData: any,
    website: any,
    brandingData: BrandingData,
    pages: any[],
    currentPageType: string,
    websiteElements: WebsiteElement[]
  ): WebsiteElement[] {
    let headerData =
      website?.json_data?.website?.header || templateData?.json_data?.website?.header;

    if (!headerData) {
      const homePage = pages?.find((p: any) => p.isHomePage);
      if (homePage && (homePage as any).jsonData)
        headerData = (homePage as any).jsonData?.page?.sections?.header;
    }
    if (!headerData) {
      headerData =
        currentJsonData?.page?.sections?.header ||
        templateData?.json_data?.page?.sections?.header ||
        templateData?.json_data?.header ||
        currentJsonData?.header;
    }

    const headerElement = websiteElements.find(e => e.id === 'header');
    if (!headerElement) {
      this.logger.warn('Header element not found');
      return websiteElements;
    }

    let navigation: any[] = [];
    if (headerData?.navigation) {
      navigation = Array.isArray(headerData.navigation)
        ? headerData.navigation.map((nav: any) => ({
            label: nav.label,
            link: nav.link,
            active: nav.active,
          }))
        : [];
    }

    const activePages = pages?.filter((p: any) => p.visible !== false) || [];

    if (
      (!navigation ||
        navigation.length === 0 ||
        this.shouldRegenerateNavigation(navigation, pages)) &&
      pages?.length > 0
    ) {
      navigation = activePages.map((page: any, index: number) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        pageType: (page as any).pageType || 'home',
        active: page.pageType === currentPageType || (index === 0 && currentPageType === 'home'),
      }));
    } else if (navigation?.length > 0) {
      navigation = navigation
        .filter((nav: any) => {
          const matchingPage = pages?.find(
            (p: any) =>
              p.name === nav.label ||
              (p as any).pageType === nav.link?.replace('#', '') ||
              nav.label.toLowerCase().includes(p.name.toLowerCase())
          );
          return matchingPage ? matchingPage.visible !== false : true;
        })
        .map((nav: any) => {
          const matchingPage = pages?.find(
            (p: any) =>
              p.name === nav.label ||
              (p as any).pageType === nav.link?.replace('#', '') ||
              nav.label.toLowerCase().includes(p.name.toLowerCase())
          );
          return {
            ...nav,
            pageType: matchingPage
              ? (matchingPage as any).pageType
              : nav.pageType || this.extractPageTypeFromLink(nav.link),
            active: matchingPage ? (matchingPage as any).pageType === currentPageType : nav.active,
          };
        });
    }

    if ((!navigation || navigation.length === 0) && pages?.length > 0) {
      navigation = activePages.map((page: any, index: number) => ({
        label: page.name,
        link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        pageType: (page as any).pageType || 'home',
        active: page.pageType === currentPageType || (index === 0 && currentPageType === 'home'),
      }));
    }

    let buttons: any = null;
    if (headerData?.cta_button) {
      const cta = headerData.cta_button;
      const navigation = linkToButtonNavigation(
        cta.navigation_target || cta.buttonNavigation || cta.link
      );
      buttons = {
        text: cta.label || 'Book Wash',
        link: buttonNavigationToHashLink(navigation),
        buttonNavigation: navigation,
        backgroundColor: cta.style?.background_color || '#3B82F6',
        textColor: cta.style?.text_color || '#FFFFFF',
      };
    } else if (headerData) {
      buttons =
        Array.isArray(headerData.buttons) && headerData.buttons.length > 0
          ? headerData.buttons[0]
          : headerData.ctaButton || headerData.buttons;
      if (buttons && !buttons.text && !buttons.label) buttons.text = 'Book Wash';
      else if (buttons?.label && !buttons.text) buttons.text = buttons.label;
      if (buttons) {
        const navigation = linkToButtonNavigation(buttons.buttonNavigation || buttons.link);
        buttons.buttonNavigation = navigation;
        buttons.link = buttonNavigationToHashLink(navigation);
      }
    }
    if (!buttons) {
      buttons = {
        text: 'Book Wash',
        link: '#express-wash-packages',
        buttonNavigation: 'express-washes',
        backgroundColor: '#1B5BFF',
        textColor: '#FFFFFF',
      };
    }

    let logo: any = { text: 'Logo', link: '/' };
    if (headerData?.logo) {
      const logoUrl = headerData.logo.url || headerData.logo.image || '';
      if (logoUrl)
        logo = {
          image: logoUrl,
          alt: headerData.logo.alt || 'Logo',
          text: headerData.logo.text || 'Logo',
          link: headerData.logo.link || '/',
        };
    }
    if (!logo.image) {
      const brandingLogoUrl =
        website?.json_data?.website?.branding?.logo?.url ||
        templateData?.json_data?.website?.branding?.logo?.url;
      if (brandingLogoUrl) {
        const bl =
          website?.json_data?.website?.branding?.logo ||
          templateData?.json_data?.website?.branding?.logo;
        logo = { image: bl.url, alt: bl.alt || 'Logo', text: bl.text || 'Logo', link: '/' };
      } else if (brandingData?.logoUrl) {
        logo = { image: brandingData.logoUrl, alt: 'Logo', text: 'Logo', link: '/' };
      }
    }

    headerElement.content = {
      ...headerElement.content,
      logo,
      navigation,
      ctaButton: buttons,
      backgroundColor: headerData?.background_color || headerData?.backgroundColor || null,
      mobileMenu: headerData?.mobileMenu !== undefined ? headerData.mobileMenu : true,
    };

    return [...websiteElements];
  }

  /**
   * Populate footer element content from template / website data.
   * Returns a new FooterData object and a spread websiteElements array for CD.
   */
  populateFooter(
    currentJsonData: any,
    templateData: any,
    website: any,
    brandingData: BrandingData,
    pages: any[],
    websiteElements: WebsiteElement[]
  ): PopulateFooterResult {
    const websiteFooter = website?.json_data?.website?.footer;
    const footerData =
      websiteFooter ||
      currentJsonData?.page?.sections?.footer ||
      templateData?.json_data?.page?.sections?.footer ||
      templateData?.json_data?.footer ||
      currentJsonData?.footer ||
      templateData?.json_data?.website?.footer;

    const footerElement = websiteElements.find(e => e.id === 'footer');
    if (!footerElement) {
      this.logger.warn('Footer element not found');
      return { footerData: {} as FooterData, websiteElements };
    }

    let quickLinks: any[] = [];
    if (footerData?.quick_links) {
      const ql = footerData.quick_links;
      if (ql.column_1)
        quickLinks = quickLinks.concat(
          ql.column_1.map((l: any) => ({ label: l.label || '', link: l.link || '#' }))
        );
      if (ql.column_2)
        quickLinks = quickLinks.concat(
          ql.column_2.map((l: any) => ({ label: l.label || '', link: l.link || '#' }))
        );
    } else if (footerData?.quickLinks) {
      quickLinks = Array.isArray(footerData.quickLinks)
        ? footerData.quickLinks.map((l: any) => ({ label: l.label, link: l.link }))
        : [];
    }
    if ((!quickLinks || quickLinks.length === 0) && pages?.length > 0) {
      const activePages = pages.filter((p: any) => p.visible !== false);
      quickLinks = activePages.map((p: any) => ({
        label: p.name,
        link: `#${p.pageType || p.name.toLowerCase().replace(/\s+/g, '-')}`,
      }));
    }
    quickLinks = resolveFooterQuickLinks(quickLinks, pages);

    let socialLinks: any[] = [];
    if (footerData?.social_links?.platforms?.length > 0) {
      socialLinks = footerData.social_links.platforms.map((s: any) => ({
        platform: s.platform || '',
        url: s.url || s.link || '#',
        icon: s.icon || s.platform?.toLowerCase() || 'link',
      }));
    } else if (footerData?.socialLinks) {
      socialLinks = Array.isArray(footerData.socialLinks)
        ? footerData.socialLinks.map((s: any) => ({
            platform: s.platform || '',
            url: s.url || s.link || '#',
            icon: s.icon || s.platform?.toLowerCase() || 'link',
          }))
        : [];
    } else if (footerData?.social_links && Array.isArray(footerData.social_links)) {
      socialLinks = footerData.social_links.map((s: any) => ({
        platform: s.platform || '',
        url: s.url || s.link || '#',
        icon: s.icon || s.platform?.toLowerCase() || 'link',
      }));
    }
    if (socialLinks.length === 0) {
      socialLinks = [
        { platform: 'Instagram', url: '#', icon: 'instagram' },
        { platform: 'Facebook', url: '#', icon: 'facebook' },
        { platform: 'YouTube', url: '#', icon: 'youtube' },
        { platform: 'LinkedIn', url: '#', icon: 'linkedin' },
        { platform: 'X', url: '#', icon: 'x' },
      ];
    }

    let logo: any = { text: 'way' };
    const footerLogoImage = footerData?.logo?.image || footerData?.logo?.url || '';
    if (footerData?.logo && footerLogoImage)
      logo = { text: footerData.logo.text || 'way', image: footerLogoImage };
    if (!logo.image) {
      const brandingLogoUrl =
        website?.json_data?.website?.branding?.logo?.url ||
        templateData?.json_data?.website?.branding?.logo?.url;
      if (brandingLogoUrl) {
        const bl =
          website?.json_data?.website?.branding?.logo ||
          templateData?.json_data?.website?.branding?.logo;
        logo = { text: bl.text || 'way', image: bl.url, alt: bl.alt || 'Logo' };
      } else if (brandingData?.logoUrl) {
        logo = { text: 'way', image: brandingData.logoUrl, alt: 'Logo' };
      }
    }

    const copyright =
      footerData?.copyright?.text ||
      (typeof footerData?.copyright === 'string' ? footerData.copyright : null) ||
      `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
    const backgroundColor =
      footerData?.theme?.background_color || footerData?.backgroundColor || '#000920';
    const textColor = footerData?.theme?.text_color || '#FFFFFF';
    const linkColor = footerData?.theme?.link_color || '#FFFFFF';
    const separatorColor = footerData?.theme?.separator_color || 'rgba(255, 255, 255, 0.1)';
    const quickLinksTitle = footerData?.quick_links?.title || 'Quick links';
    const socialLinksTitle = footerData?.social_links?.title || 'Connect with us';

    footerElement.content = {
      ...footerElement.content,
      logo: { ...logo },
      quickLinks,
      quickLinksTitle,
      socialLinks,
      socialLinksTitle,
      copyright,
      backgroundColor,
      textColor,
      linkColor,
      separatorColor,
    } as any;

    const newFooterData: FooterData = {
      logo,
      quickLinks,
      quickLinksTitle,
      socialLinks,
      socialLinksTitle,
      copyright,
      backgroundColor,
    } as any;

    return { footerData: newFooterData, websiteElements: [...websiteElements] };
  }

  /** Normalize contact detail fields that may be a string or { value, label }. */
  private extractContactDetailString(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      return value.value || value.phone || value.email || value.label || '';
    }
    return String(value);
  }

  /** Extract text editor style from API title.style (direct props or nested text_style object). */
  extractTitleStyleFromApi(styleObj: any): any | null {
    if (!styleObj || typeof styleObj !== 'object') return null;
    const textStyle = styleObj.text_style;
    if (textStyle && typeof textStyle === 'object') {
      return {
        fontWeight: textStyle.fontWeight || null,
        fontStyle: textStyle.fontStyle || null,
        textDecoration: textStyle.textDecoration || null,
        textAlign: textStyle.textAlign || null,
      };
    }
    if (
      styleObj.fontWeight ||
      styleObj.fontStyle ||
      styleObj.textDecoration ||
      styleObj.textAlign
    ) {
      return {
        fontWeight: styleObj.fontWeight || null,
        fontStyle: styleObj.fontStyle || null,
        textDecoration: styleObj.textDecoration || null,
        textAlign: styleObj.textAlign || null,
      };
    }
    return null;
  }

  /** Extract display title from API title field (string or { label, text }). */
  extractTitleText(title: any): string {
    if (!title) return '';
    if (typeof title === 'string') return title;
    if (typeof title === 'object') return title.label || title.text || '';
    return String(title);
  }
}
